/* ==========================================================================
   Healeo native cart drawer
   Replaces the iCart app.

   Two rules shaped this file:

   1. All markup comes from Liquid via the Section Rendering API, never from
      string templates here. That is what keeps the drawer's numbers identical
      to the ones Shopify will charge: the discount that a merchant configures
      under Discounts > Automatic is already reflected in `cart.items[].
      final_line_price`, so rendering the real cart is the whole correctness
      story. iCart drew its own prices client-side, which is why its "FREE
      Melatonin" row never existed in /cart.js.

   2. The theme, and several apps, add to the cart through their own AJAX
      calls. Rather than guess at every button selector, we wrap fetch and
      XMLHttpRequest and react to any request that hits a /cart/ write
      endpoint. Anything that changes the cart therefore refreshes the drawer,
      including apps we do not control.
   ========================================================================== */
(function () {
  'use strict';

  var ROOT_ID = 'hn-cart-drawer';
  var SECTION_ID = 'hn-cart-drawer';
  var GIFT_PROP = '_hn_gift_tier';
  var WRITE_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  var state = {
    root: null,
    open: false,
    busy: false,
    lastFocus: null,
    reconciling: false,
    suppress: 0,
    settings: {},
    booted: false
  };

  /* ----------------------------------------------------------- utilities */

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function readSettings() {
    var el = document.getElementById('hn-cart-settings');
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  }

  function routes() {
    var r = (window.Shopify && window.Shopify.routes) || {};
    var base = r.root || '/';
    if (base.slice(-1) !== '/') base += '/';
    return {
      cart: base + 'cart.js',
      add: base + 'cart/add.js',
      change: base + 'cart/change.js',
      base: base
    };
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw Object.assign(new Error(data.description || res.statusText), { data: data });
        return data;
      });
    });
  }

  function getCart() {
    return fetch(routes().cart, { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  /* -------------------------------------------------------------- render */

  /* The section is rendered into the layout, so its wrapper id is stable.
     Shopify returns the whole `<div id="shopify-section-...">`; we swap only
     its children so the drawer element itself — and its open state — survive. */
  function applySections(sections) {
    if (!sections) return false;
    var html = sections[SECTION_ID];
    if (typeof html !== 'string') return false;
    var host = document.getElementById('shopify-section-' + SECTION_ID);
    if (!host) return false;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.getElementById('shopify-section-' + SECTION_ID) || doc.body.firstElementChild;
    if (!fresh) return false;
    var wasOpen = state.open;
    host.innerHTML = fresh.innerHTML;
    bind();
    if (wasOpen) markOpen(true);
    return true;
  }

  function refresh() {
    return fetch(routes().base + '?sections=' + SECTION_ID, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(applySections)
      .catch(function () { return false; });
  }

  function mutate(body) {
    setBusy(true);
    body.sections = SECTION_ID;
    body.sections_url = window.location.pathname + window.location.search;
    return postJSON(routes().change, body)
      .then(function (data) {
        applySections(data.sections);
        return reconcileGifts(data);
      })
      .catch(function (err) {
        // A failed line change leaves the drawer showing stale numbers, so
        // always fall back to a full re-render rather than leaving it as-is.
        console.warn('[hn-cart]', err && err.message);
        return refresh();
      })
      .then(function () { setBusy(false); });
  }

  function setBusy(v) {
    state.busy = v;
    var el = $('.hn-cart');
    if (el) el.setAttribute('data-busy', v ? 'true' : 'false');
  }

  /* --------------------------------------------------------- gift tiers */

  /* Threshold maths must ignore the gift lines themselves, or adding a gift
     can push the subtotal over the next tier and the drawer starts handing
     out prizes for prizes. */
  function giftSubtotal(cart) {
    var giftVariants = {};
    tiers().forEach(function (t) { giftVariants[String(t.variant_id)] = true; });
    return cart.items.reduce(function (sum, item) {
      if ((item.properties || {})[GIFT_PROP]) return sum;
      if (giftVariants[String(item.variant_id)]) return sum;
      return sum + item.final_line_price;
    }, 0);
  }

  function tiers() {
    return (state.settings.tiers || []).filter(function (t) { return t.variant_id && t.threshold > 0; })
      .sort(function (a, b) { return a.threshold - b.threshold; });
  }

  function reconcileGifts(cartLike) {
    if (state.reconciling) return Promise.resolve({ removed: 0, added: 0 });
    var list = tiers();
    if (!list.length) return Promise.resolve({ removed: 0, added: 0 });

    return (cartLike && cartLike.items ? Promise.resolve(cartLike) : getCart()).then(function (cart) {
      var subtotal = giftSubtotal(cart);
      var earned = {};
      list.forEach(function (t) { if (subtotal >= t.threshold) earned[String(t.variant_id)] = t; });

      // Every variant configured as a gift, earned or not. A ₹0 gift variant is
      // orderable like anything else, so a line is treated as a gift by its
      // variant id and not only by the property we stamp on it — otherwise
      // somebody who added the free variant directly would keep it in a cart
      // that never reached the threshold.
      var giftVariants = {};
      list.forEach(function (t) { giftVariants[String(t.variant_id)] = true; });

      var present = {};
      var removals = [];
      cart.items.forEach(function (item) {
        var vid = String(item.variant_id);
        var tagged = !!(item.properties || {})[GIFT_PROP];
        if (!tagged && !giftVariants[vid]) return;
        if (!earned[vid] || present[vid]) {
          removals.push(item.key);          // tier lost, unearned, or a duplicate
        } else {
          present[vid] = true;
          if (item.quantity !== 1) removals.push({ key: item.key, quantity: 1 });
        }
      });

      var additions = Object.keys(earned).filter(function (vid) { return !present[vid]; })
        .map(function (vid) { return earned[vid]; });

      if (!removals.length && !additions.length) return { removed: 0, added: 0 };

      state.reconciling = true;
      var chain = Promise.resolve();

      removals.forEach(function (r) {
        chain = chain.then(function () {
          return postJSON(routes().change, typeof r === 'string' ? { id: r, quantity: 0 } : { id: r.key, quantity: r.quantity });
        });
      });

      if (additions.length) {
        chain = chain.then(function () {
          return postJSON(routes().add, {
            items: additions.map(function (t) {
              var props = {};
              props[GIFT_PROP] = String(t.threshold);
              return { id: t.variant_id, quantity: 1, properties: props };
            })
          });
        });
      }

      return chain
        .catch(function (err) { console.warn('[hn-cart] gift reconcile', err && err.message); })
        .then(function () {
          state.reconciling = false;
          return refresh();
        })
        .then(function () { return { removed: removals.length, added: additions.length }; });
    });
  }

  /* -------------------------------------------------------------- open/close */

  function markOpen(v) {
    var root = document.getElementById(ROOT_ID);
    if (!root) return;
    state.open = v;
    root.hidden = false;
    root.classList.toggle('is-open', v);
    root.setAttribute('aria-hidden', v ? 'false' : 'true');
    document.documentElement.classList.toggle('hn-cart-locked', v);
    if (v) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }

  /* `skipRefresh` exists for the add-to-cart path, which already has fresher
     markup on the way and would otherwise fire a second render fetch. */
  function open(opts) {
    if (state.open) return;
    state.lastFocus = document.activeElement;
    markOpen(true);
    var panel = $('.hn-panel');
    if (panel) {
      var target = panel.querySelector('.hn-iconbtn') || panel;
      window.requestAnimationFrame(function () { target.focus({ preventScroll: true }); });
    }
    if (!(opts && opts.skipRefresh)) refresh();
  }

  function close() {
    if (!state.open) return;
    markOpen(false);
    if (state.lastFocus && state.lastFocus.focus) state.lastFocus.focus({ preventScroll: true });
  }

  function trapFocus(e) {
    if (!state.open || e.key !== 'Tab') return;
    var panel = $('.hn-panel');
    if (!panel) return;
    var items = $$('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])', panel)
      .filter(function (el) { return el.offsetParent !== null; });
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* -------------------------------------------------------------- binding */

  function bind() {
    var root = document.getElementById(ROOT_ID);
    if (!root || root.dataset.hnBound === '1') return;
    root.dataset.hnBound = '1';

    root.addEventListener('click', function (e) {
      var el = e.target.closest('[data-hn]');
      if (!el) return;
      var action = el.getAttribute('data-hn');

      if (action === 'close' || action === 'scrim') { e.preventDefault(); close(); return; }

      if (action === 'qty') {
        e.preventDefault();
        var key = el.getAttribute('data-key');
        var next = parseInt(el.getAttribute('data-value'), 10);
        if (!key || isNaN(next) || next < 0) return;
        mutate({ id: key, quantity: next });
        return;
      }

      if (action === 'remove') {
        e.preventDefault();
        mutate({ id: el.getAttribute('data-key'), quantity: 0 });
        return;
      }

      if (action === 'add') {
        e.preventDefault();
        var id = parseInt(el.getAttribute('data-variant'), 10);
        if (!id) return;
        el.setAttribute('disabled', 'disabled');
        setBusy(true);
        postJSON(routes().add, { items: [{ id: id, quantity: 1 }] })
          .then(function () { return refresh(); })
          .then(function () { return reconcileGifts(); })
          .catch(function (err) { console.warn('[hn-cart] add', err && err.message); })
          .then(function () { setBusy(false); });
      }
    });

    root.addEventListener('change', function (e) {
      var input = e.target.closest('input[data-hn-qty-input]');
      if (!input) return;
      var next = parseInt(input.value, 10);
      if (isNaN(next) || next < 0) { refresh(); return; }
      mutate({ id: input.getAttribute('data-key'), quantity: next });
    });

    startTimers(root);
  }

  /* Countdown is per-visit rather than a fixed date: a promo that reads
     "expired" to every visitor for the rest of the campaign is worse than one
     that restarts. The deadline is kept in sessionStorage so a page change
     does not gift the shopper a fresh window. */
  function startTimers(root) {
    $$('[data-hn-countdown]', root).forEach(function (el) {
      if (el.dataset.hnTicking === '1') return;
      el.dataset.hnTicking = '1';
      var minutes = parseInt(el.getAttribute('data-hn-countdown'), 10) || 5;
      var storeKey = 'hn-countdown-' + (el.getAttribute('data-hn-countdown-key') || 'default');
      var ends = parseInt(window.sessionStorage.getItem(storeKey), 10);
      var now = Date.now();
      if (!ends || isNaN(ends) || ends < now) {
        ends = now + minutes * 60000;
        try { window.sessionStorage.setItem(storeKey, String(ends)); } catch (e) { /* private mode */ }
      }
      var mm = el.querySelector('[data-hn-min]');
      var ss = el.querySelector('[data-hn-sec]');
      var tick = function () {
        var left = ends - Date.now();
        if (left <= 0) {
          // A timer frozen at 00:00 advertises an offer that has visibly
          // expired, which is worse than no timer. Roll the window instead.
          ends = Date.now() + minutes * 60000;
          try { window.sessionStorage.setItem(storeKey, String(ends)); } catch (e) { /* private mode */ }
          left = ends - Date.now();
        }
        var m = Math.floor(left / 60000);
        var s = Math.floor((left % 60000) / 1000);
        if (mm) mm.textContent = String(m).padStart(2, '0');
        if (ss) ss.textContent = String(s).padStart(2, '0');
      };
      window.setInterval(tick, 1000);
      tick();
    });
  }

  /* --------------------------------------------- intercept cart writes */

  function afterWrite(url) {
    // Our own calls already render and reconcile; re-entering here would fire
    // a second render for every add and undo the round trip we just saved.
    if (state.reconciling || state.suppress > 0) return;
    var isAdd = /\/cart\/add/.test(url);
    window.setTimeout(function () {
      refresh().then(function () { return reconcileGifts(); }).then(function () {
        if (isAdd && state.settings.open_on_add) open();
      });
    }, 60);
  }

  function patchNetwork() {
    var nativeFetch = window.fetch;
    if (typeof nativeFetch === 'function') {
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var p = nativeFetch.apply(this, arguments);
        if (WRITE_RE.test(url)) p.then(function (res) { if (res && res.ok) afterWrite(url); }).catch(function () {});
        return p;
      };
    }
    var open_ = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__hnUrl = url;
      return open_.apply(this, arguments);
    };
    var send_ = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      xhr.addEventListener('load', function () {
        if (xhr.status >= 200 && xhr.status < 300 && WRITE_RE.test(xhr.__hnUrl || '')) afterWrite(xhr.__hnUrl);
      });
      return send_.apply(this, arguments);
    };
  }

  /* Add to cart must open this drawer, never navigate.

     Watching the network alone was not enough: the theme binds its own click
     handler to `.js_frm_cart`, and that handler decides where the shopper goes
     afterwards — on this store it can end up at /cart, which is exactly what
     the drawer exists to avoid. So the click is taken in the capture phase and
     stopped before the theme ever sees it.

     The request is built from the form's own FormData rather than a variant id
     we guess at, which keeps the selected variant, the quantity and any line
     item properties other apps have injected into that form. */
  function addFromForm(form, btn) {
    /* Open first, fetch second.

       The drawer used to appear only after add -> render -> cart -> render,
       three or four round trips deep, so on a phone the tap felt dead for a
       second or more. Now the panel slides in on the tap with the previous
       cart still in it and a busy state over the top, and `sections` on the
       add request means the new markup arrives with the add itself rather
       than in a follow-up fetch. The gift check runs after the shopper can
       already see the panel. */
    var fd = new FormData(form);
    fd.append('sections', SECTION_ID);
    fd.append('sections_url', window.location.pathname + window.location.search);

    setBusy(true);
    if (btn) btn.setAttribute('aria-busy', 'true');
    if (state.settings.open_on_add !== false) open({ skipRefresh: true });

    state.suppress++;
    return fetch(routes().add, { method: 'POST', body: fd, headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json().then(function (d) { if (!r.ok) throw new Error(d.description || r.statusText); return d; }); })
      .then(function (data) {
        if (!applySections(data.sections)) return refresh();
      })
      .then(function () { setBusy(false); return reconcileGifts(); })
      .catch(function (err) {
        // Sold out, or a variant the form did not carry: let the theme's own
        // path run so the shopper still sees whatever error it shows.
        console.warn('[hn-cart] add', err && err.message);
        close();
        form.submit();
      })
      .then(function () {
        state.suppress--;
        setBusy(false);
        if (btn) btn.removeAttribute('aria-busy');
      });
  }

  function interceptAddButtons() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('button, input[type="submit"]');
      if (!btn || btn.disabled) return;
      var form = btn.closest('form[action*="/cart/add"]');
      if (!form || form.hasAttribute('data-hn-ignore')) return;
      // Buy now / dynamic checkout buttons belong to checkout, not the drawer.
      var name = btn.getAttribute('name') || '';
      if (name === 'checkout' || /shopify-payment-button|dynamic-checkout|buy[-_ ]?now/i.test(btn.className + ' ' + name)) return;
      if (btn.closest('.shopify-payment-button')) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      addFromForm(form, btn);
    }, true);
  }

  /* Plain (non-AJAX) add-to-cart forms would navigate away to /cart. Submit
     them through the API instead so the shopper stays on the page. */
  function interceptForms() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      var action = form.getAttribute('action') || '';
      if (!/\/cart\/add/.test(action)) return;
      if (form.hasAttribute('data-hn-ignore')) return;
      e.preventDefault();
      setBusy(true);
      fetch(routes().add, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function () { return refresh(); })
        .then(function () { return reconcileGifts(); })
        .catch(function (err) { console.warn('[hn-cart] form add', err && err.message); })
        .then(function () { setBusy(false); if (state.settings.open_on_add) open(); });
    }, true);
  }

  /* Anything that used to open the theme's own drawer, or link to /cart,
     opens this one instead. */
  function interceptOpeners() {
    document.addEventListener('click', function (e) {
      var el = e.target.closest('a[href$="/cart"], a[href*="/cart?"], [data-id="#nt_cart_canvas"], .icon_cart a, [data-hn-open]');
      if (!el) return;
      if (el.hasAttribute('data-hn-ignore')) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      if (state.settings.intercept_cart_links === false) return;
      e.preventDefault();
      e.stopPropagation();
      open();
    }, true);
  }


  /* Checkout has to go through Shopflo, not Shopify.

     The theme wires Shopflo up by selector — layout/theme.liquid registers
     `#icartMainContent`/`.icart-checkout-btn` and `.icart-pg`/`.icart-pg-checkout`,
     and Shopflo hides the native button and inserts its own in front of it.
     Neither selector exists in this drawer, so the native `name="checkout"`
     submit fell straight through to Shopify's checkout.

     Rather than register the drawer with that observer (which would inject a
     button carrying the old theme's classes into a panel styled nothing like
     it), the drawer's own button calls the same global the injected one does.
     If Shopflo has not loaded, or its checkout is switched off, the form
     submits as before — a shopper is never left with a dead button. */
  function shopfloReady() {
    if (typeof window.handleFloCheckoutBtn !== 'function') return false;
    var flo = window.Shopflo;
    if (flo && flo.isFloCheckoutEnabled === false) return false;
    return true;
  }

  function proceedToCheckout(form) {
    if (shopfloReady()) {
      try { window.handleFloCheckoutBtn(); return; }
      catch (err) { console.warn('[hn-cart] shopflo checkout', err && err.message); }
    }
    if (form) form.submit();
  }

  function notice(text) {
    var host = $('.hn-foot__inner');
    if (!host) return;
    var el = host.querySelector('[data-hn-notice]');
    if (!el) {
      el = document.createElement('p');
      el.setAttribute('data-hn-notice', '');
      el.className = 'hn-notice';
      host.insertBefore(el, host.firstChild);
    }
    el.textContent = text;
    window.clearTimeout(el._t);
    el._t = window.setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 6000);
  }

  /* Checkout re-checks the gift ladder before handing off.

     A ₹0 gift product is orderable by anyone who has its variant id, and the
     periodic reconcile only runs on cart changes — so a cart could in principle
     reach the checkout button holding a gift it never earned. This is the last
     gate: reconcile, and if that had to take anything away, stop and show why
     rather than sending a wrong basket to Shopflo. */
  function interceptCheckout() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-hn="checkout"]');
      if (!btn) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      var form = btn.closest('form');
      setBusy(true);
      reconcileGifts()
        .catch(function () { return { removed: 0, added: 0 }; })
        .then(function (res) {
          setBusy(false);
          if (res && res.removed) {
            open();
            notice(state.settings.gift_removed_notice ||
                   'A free gift was removed because your cart no longer qualifies. Check your total and continue.');
            return;
          }
          proceedToCheckout(form);
        });
    }, true);
  }

  /* ---------------------------------------------------------------- boot */

  function boot() {
    if (state.booted) return;
    state.booted = true;
    state.settings = readSettings();
    bind();
    // The /cart page renders the same blocks outside the drawer root, so its
    // countdown never reached startTimers() and sat frozen at 00:00.
    startTimers(document);
    patchNetwork();
    interceptAddButtons();
    interceptForms();
    interceptOpeners();

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
      trapFocus(e);
    });

    interceptCheckout();

    window.HNCart = { open: open, close: close, refresh: refresh, reconcile: reconcileGifts };

    // The cart may already qualify for a gift when the page loads.
    reconcileGifts();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
